/**
 * Erreurs métier. Chaque erreur porte un code stable, utilisé par les tests et
 * par l'interface, et un message rédigé en français conformément à ENF-012.
 *
 * Les situations couvertes sont celles de la section 17 de la spécification.
 */

export type ErrorCode =
  // Parcours GPX — EF-GPX-001, EF-GPX-002
  | 'gpx/extension'
  | 'gpx/malformed'
  | 'gpx/not-gpx-11'
  | 'gpx/too-large'
  | 'gpx/too-many-points'
  | 'gpx/no-usable-track'
  | 'gpx/too-short'
  // Recherche — EF-POI-009, EF-POI-012
  | 'search/network'
  | 'search/http'
  | 'search/timeout'
  | 'search/truncated'
  | 'search/cancelled'
  | 'search/budget-exhausted'
  | 'search/result-cap'
  | 'search/offline'
  // Paramètres — EF-CAL-001, EF-POI-002, EF-POI-003
  | 'params/invalid'
  // Documents — EF-RBK-005, EF-PAR-003, annexes A et B
  | 'document/too-large'
  | 'document/not-json'
  | 'document/unknown-format'
  | 'document/unknown-schema-version'
  | 'document/schema'
  | 'document/invariant'
  // Stockage — EF-FIC-001
  | 'storage/unavailable'
  | 'storage/quota'
  | 'storage/write-failed'
  | 'storage/read-only'
  // Concurrence — EF-NAV-005
  | 'operation/busy'
  // Mode course — EF-MOD-001, EF-MOD-004
  | 'race/no-result'
  | 'race/geolocation-unavailable'
  | 'race/permission-denied';

export class DomainError extends Error {
  readonly code: ErrorCode;
  /** Détail diagnosticable : nom du champ, ressource ou invariant fautif. */
  readonly detail: string | null;

  constructor(code: ErrorCode, message: string, detail: string | null = null) {
    super(message);
    this.name = 'DomainError';
    this.code = code;
    this.detail = detail;
  }
}

export function isDomainError(value: unknown): value is DomainError {
  return value instanceof DomainError;
}

/** Message affichable pour une valeur levée quelconque. */
export function describeError(value: unknown): string {
  if (isDomainError(value)) return value.message;
  if (value instanceof Error) return value.message;
  return String(value);
}

/**
 * Résultat d'une opération pouvant échouer sans exception. Utilisé aux
 * frontières où l'échec est une issue attendue : validation de document,
 * validation de paramètres, réponse Overpass.
 */
export type Result<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: DomainError };

export function ok<T>(value: T): Result<T> {
  return { ok: true, value };
}

export function fail<T>(error: DomainError): Result<T> {
  return { ok: false, error };
}
